import React, { useState } from 'react';
import axios from '../../../../api/myAxios';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  useTheme,
  Alert,
  Fade,
  CircularProgress,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const DataFileUpload = ({ workspaceId, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [isFilePublic, setIsFilePublic] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const theme = useTheme();

  const uploadFileMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post('/api/data/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log(t('dataFileUpload.uploadSuccess'), data);
      queryClient.invalidateQueries(['dataItems', workspaceId]);
      setFile(null);
      setDescription('');
      setIsFilePublic(false);
      setHasAttemptedSubmit(false);
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error(t('error.console.errorUploadingFile'), error);
    },
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!file) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('workspaceId', workspaceId);
    formData.append('isFilePublic', isFilePublic);
    formData.append('name', file.name);
    formData.append('description', description);

    uploadFileMutation.mutate(formData);
  };

  const getFileError = () => {
    if (!hasAttemptedSubmit) return null;
    if (!file) return t('dataFileUpload.errorNoFile');
    if (file.size > 5 * 1024 * 1024)
      return t('dataFileUpload.errorFileTooLarge');
    return null;
  };

  const fileError = getFileError();
  const isFormValid = file && !fileError && !uploadFileMutation.isLoading;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          bgcolor: dragActive ? theme.palette.primary.light : 'transparent',
          transition: 'all 0.2s ease',
          opacity: uploadFileMutation.isLoading ? 0.7 : 1,
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={uploadFileMutation.isLoading}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t('dataFileUpload.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('dataFileUpload.or')}
        </Typography>
        <Button
          variant="outlined"
          component="label"
          htmlFor="file-upload"
          disabled={uploadFileMutation.isLoading}
        >
          {t('dataFileUpload.browseFiles')}
        </Button>
        {file && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('dataFileUpload.selectedFile')}: <strong>{file.name}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(file.size / (1024 * 1024)).toFixed(2)} MB
            </Typography>
          </Box>
        )}
      </Box>

      <Box component="form" onSubmit={handleUpload} sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label={t('dataFileUpload.description')}
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={uploadFileMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isFilePublic}
              onChange={(e) => setIsFilePublic(e.target.checked)}
              disabled={uploadFileMutation.isLoading}
            />
          }
          label={t('dataFileUpload.makePublic')}
          sx={{ mb: 2 }}
        />

        {fileError && (
          <FormHelperText error sx={{ mb: 2 }}>
            {fileError}
          </FormHelperText>
        )}

        {uploadFileMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadFileMutation.error?.response?.data?.message ||
              t('dataFileUpload.errorUploading')}
          </Alert>
        )}

        {uploadFileMutation.isSuccess && (
          <Fade in={uploadFileMutation.isSuccess} timeout={1000}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('dataFileUpload.uploadSuccess')}
            </Alert>
          </Fade>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!isFormValid}
          fullWidth
          startIcon={
            uploadFileMutation.isLoading ? <CircularProgress size={20} /> : null
          }
        >
          {uploadFileMutation.isLoading
            ? t('dataFileUpload.uploading')
            : t('dataFileUpload.uploadFile')}
        </Button>
      </Box>
    </Paper>
  );
};

export default DataFileUpload;
